import { useId, type ComponentProps, type ReactNode } from "react";
import classNames from "classnames";
import { Switch } from "@design-system/Switch/Switch";
import styles from "./SettingsPanel.module.scss";

/** Whether a slot renders anything: `{cond && <X />}` can pass `false`, `null`, `undefined` or "". */
export const hasSlotContent = (node: ReactNode): boolean =>
    node !== undefined &&
    node !== null &&
    node !== "" &&
    typeof node !== "boolean";

export interface SettingsPanelSectionProps {
    /** Title above the rows; it also names the group. */
    label?: ReactNode;
    children: ReactNode;
}

/** A group of rows under an optional title. */
export const SettingsPanelSection = ({
    label,
    children,
}: SettingsPanelSectionProps) => {
    const titleId = useId();
    const hasLabel = hasSlotContent(label);

    return (
        <div
            role="group"
            aria-labelledby={hasLabel ? titleId : undefined}
            className={styles.section}
        >
            {hasLabel && (
                <span id={titleId} className={styles.sectionTitle}>
                    {label}
                </span>
            )}
            {children}
        </div>
    );
};

/** A full-width line between sections or rows. */
export const SettingsPanelSeparator = () => <hr className={styles.separator} />;

export interface SettingsPanelRowProps {
    label: ReactNode;
    /** Id on the label, so a control in `children` can point `aria-labelledby` at it. */
    labelId?: string;
    /** A note under the row. */
    hint?: ReactNode;
    /** Controls, placed at the row's end. */
    children?: ReactNode;
    className?: string;
}

/** One setting: its label at the start (ellipsized) and its controls at the end. */
export const SettingsPanelRow = ({
    label,
    labelId,
    hint,
    children,
    className,
}: SettingsPanelRowProps) => (
    <>
        <div className={classNames(styles.row, className)}>
            <span id={labelId} className={styles.rowLabel}>
                {label}
            </span>
            {hasSlotContent(children) && (
                <div className={styles.rowControls}>{children}</div>
            )}
        </div>
        {hasSlotContent(hint) && <div className={styles.hint}>{hint}</div>}
    </>
);

export interface SettingsPanelToggleRowProps {
    label: ReactNode;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
}

/** An on/off setting; the whole row is the switch's label, so any click toggles. */
export const SettingsPanelToggleRow = ({
    label,
    checked,
    onCheckedChange,
    disabled = false,
}: SettingsPanelToggleRowProps) => (
    <Switch
        size="sm"
        labelPlacement="start"
        label={<span className={styles.rowLabel}>{label}</span>}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={classNames(styles.row, styles.toggleRow)}
    />
);

/** A non-interactive line in place of rows, such as "All columns are visible". */
export const SettingsPanelEmpty = ({ children }: { children: ReactNode }) => (
    <div className={styles.empty}>{children}</div>
);

export type SettingsPanelActionProps = Omit<ComponentProps<"button">, "type">;

/** A full-width row button for the footer, such as "Reset". */
export const SettingsPanelAction = ({
    className,
    ...props
}: SettingsPanelActionProps) => (
    <button
        type="button"
        className={classNames(styles.row, styles.action, className)}
        {...props}
    />
);
